// Settings → Register (Clover) (decision 159, owner): which of Clover's categories the wholesale app keeps. A soda sold
// by the piece at the counter has no business here (JL); its lines are taken out and never pulled again.
import type { CloverCategories } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth';
import { ErrorBox, QueryView } from '../components/ui';
import { formatMoney, formatStamp } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

export function CloverSettingsPage() {
  const { t } = useLocale();
  const query = useQuery({ queryKey: qk.cloverCategories, queryFn: ({ signal }) => api.cloverCategories(signal) });
  return (
    <div className="desk-page stack" data-testid="clover-settings">
      <header className="desk-head">
        <div>
          <h1>{t('cloverTitle')}</h1>
          <p className="muted">{t('cloverHint')}</p>
        </div>
      </header>
      <QueryView query={query}>
        {(data) =>
          data.configured ? (
            <>
              <RegisterStock />
              <Choose data={data} />
            </>
          ) : (
            <p className="note">{t('cloverNotConnected')}</p>
          )
        }
      </QueryView>
    </div>
  );
}

/** The register's sales off the hub's stock (decision 201): on from a moment (the night count), what it took, what it could not. */
function RegisterStock() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: qk.registerStock, queryFn: ({ signal }) => api.registerStock(signal) });
  const set = useMutation({ mutationFn: (on: boolean) => api.setRegisterStock(on), onSuccess: (r) => qc.setQueryData(qk.registerStock, r) });
  const s = q.data;
  if (!s) return q.error ? <ErrorBox error={q.error} /> : null;
  const skipped = s.skipped.no_product + s.skipped.no_unit + s.skipped.no_content + s.skipped.unit_mismatch + s.skipped.too_small;
  return (
    <section className="panel stack-sm" data-testid="register-stock">
      <h2>{t('registerStockTitle')}</h2>
      <p className="muted">{t('registerStockHint')}</p>
      {s.on && s.from ? (
        <>
          <p className="ok-band">{t('registerStockOn').replace('{when}', formatStamp(s.from, intlLocale)).replace('{n}', s.boxesTaken.toLocaleString(intlLocale, { maximumFractionDigits: 1 }))}</p>
          {skipped ? (
            <p className="note" data-testid="register-stock-skipped">
              {t('registerStockSkipped')
                .replace('{product}', String(s.skipped.no_product))
                .replace('{content}', String(s.skipped.no_content + s.skipped.no_unit))
                .replace('{small}', String(s.skipped.too_small))}
              {s.skipped.unit_mismatch ? ` ${t('registerStockMismatch').replace('{n}', String(s.skipped.unit_mismatch))}` : null}
            </p>
          ) : null}
        </>
      ) : (
        <p className="note">{t('registerStockOff')}</p>
      )}
      {me?.role === 'owner' ? (
        <button type="button" className={s.on ? 'btn' : 'btn btn-primary'} disabled={set.isPending} onClick={() => (s.on || window.confirm(t('registerStockConfirm'))) && set.mutate(!s.on)}>
          {s.on ? t('registerStockTurnOff') : t('registerStockTurnOn')}
        </button>
      ) : null}
      {set.error ? <ErrorBox error={set.error} /> : null}
    </section>
  );
}

function Choose({ data }: { data: CloverCategories }) {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const owner = me?.role === 'owner';
  const [kept, setKept] = useState<Set<string>>(() => new Set(data.categories.filter((c) => c.kept).map((c) => c.id)));
  const [none, setNone] = useState(data.uncategorized.kept);
  const [said, setSaid] = useState<string | null>(null);
  // the ticks are the owner's until saved: a refetch (an hourly pull changing the sales) does not reset them
  const save = useMutation({
    mutationFn: (keepAll: boolean) => api.saveCloverFilter({ keepAll, categories: [...kept], uncategorized: none }),
    onSuccess: (r) => {
      qc.setQueryData(qk.cloverCategories, r.status);
      setKept(new Set(r.status.categories.filter((c) => c.kept).map((c) => c.id)));
      setNone(r.status.uncategorized.kept);
      setSaid((r.readAgain ? t('cloverSavedAgain') : t('cloverSaved')).replace('{n}', String(r.removed)));
      void qc.invalidateQueries({ queryKey: qk.unknownNames });
      void qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
  const money = (n: number) => formatMoney(n, intlLocale);
  const toggle = (id: string) =>
    setKept((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const keptSold = data.categories.filter((c) => kept.has(c.id)).reduce((a, c) => a + c.sold90, 0) + (none ? data.uncategorized.sold90 : 0);
  return (
    <section className="stack">
      <p>
        {data.filter ? t('cloverFiltered').replace('{n}', String(data.filter.categories.length)) : t('cloverAll')}
        {data.lastPullAt ? <span className="muted"> · {t('cloverLastPull')} {formatStamp(data.lastPullAt, intlLocale)}</span> : null}
      </p>
      <div className="table-wrap">
        <table className="data-table clover-table" aria-label={t('cloverTitle')}>
          <thead>
            <tr>
              <th>{t('cloverKeep')}</th>
              <th>{t('cloverCategory')}</th>
              <th className="num">{t('cloverItems')}</th>
              <th className="num">{t('cloverSold90')}</th>
            </tr>
          </thead>
          <tbody>
            {data.categories.map((c) => (
              <tr key={c.id}>
                <td>
                  <input type="checkbox" aria-label={c.name} checked={kept.has(c.id)} disabled={!owner} onChange={() => toggle(c.id)} />
                </td>
                <td>
                  {c.name}
                  {c.isNew ? <span className="badge badge-warn">{t('cloverNew')}</span> : null}
                </td>
                <td className="num">{c.items}</td>
                <td className="num">{money(c.sold90)}</td>
              </tr>
            ))}
            <tr>
              <td>
                <input type="checkbox" aria-label={t('cloverNoCategory')} checked={none} disabled={!owner} onChange={() => setNone((v) => !v)} />
              </td>
              <td>
                <em>{t('cloverNoCategory')}</em>
              </td>
              <td className="num">{data.uncategorized.items}</td>
              <td className="num">{money(data.uncategorized.sold90)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="muted">{t('cloverKeptSold').replace('{kept}', money(keptSold)).replace('{all}', money(data.sold90))}</p>
      {owner ? (
        <div className="toolbar">
          <button type="button" className="btn btn-primary" disabled={save.isPending || (kept.size === 0 && !none)} onClick={() => window.confirm(t('cloverAsk')) && save.mutate(false)}>
            {t('cloverSave')}
          </button>
          {data.filter ? (
            <button type="button" className="btn btn-secondary" disabled={save.isPending} onClick={() => save.mutate(true)}>
              {t('cloverKeepAll')}
            </button>
          ) : null}
        </div>
      ) : (
        <p className="note">{t('cloverOwnerOnly')}</p>
      )}
      {said ? <p className="ok-band" role="status">{said}</p> : null}
      {save.error ? <ErrorBox error={save.error} /> : null}
    </section>
  );
}
